import React, { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DeleteAccountModal — irreversible self-service deletion.
 *
 * Two-factor confirmation:
 *   1. User re-enters their current password (defends against a stolen session)
 *   2. User types "DELETE" into a plain-text field (defends against a slip tap)
 *
 * On success we wipe local storage, drop the offline cache, and redirect to
 * the login screen with a friendly toast query param.
 */
const DeleteAccountModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit =
    password.length > 0 && confirmText.trim().toUpperCase() === 'DELETE' && !busy;

  const handleClose = () => {
    if (busy) return;
    setPassword('');
    setConfirmText('');
    setError('');
    onClose();
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const resp = await fetch(`${API}/auth/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ current_password: password, confirm_text: confirmText }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Deletion failed');

      // Wipe everything client-side.
      try {
        Object.keys(localStorage).forEach((k) => localStorage.removeItem(k));
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        }
        if (window.indexedDB && window.indexedDB.databases) {
          const dbs = await window.indexedDB.databases();
          dbs.forEach((d) => d.name && window.indexedDB.deleteDatabase(d.name));
        }
      } catch (_e) { /* ignore best-effort cleanup errors */ }

      window.location.href = '/login?deleted=1';
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('deleteAccountTitle') || 'Delete account'} size="md">
      <form onSubmit={handleDelete} className="p-6 space-y-5" data-testid="delete-account-form">
        {/* Warning banner */}
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <span aria-hidden className="text-2xl">⚠️</span>
            <div className="text-sm text-red-800 leading-relaxed">
              <p className="font-semibold">{t('deleteAccountWarnTitle') || 'This action cannot be undone.'}</p>
              <p className="mt-1">
                {t('deleteAccountWarnBody') ||
                  'All your tasks, contacts, attachments, share links and settings will be permanently deleted. Any active subscription will be cancelled.'}
              </p>
            </div>
          </div>
        </div>

        {/* Password re-entry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('deleteAccountPasswordLabel') || 'Confirm your password'}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="delete-account-password"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>

        {/* Type DELETE */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('deleteAccountTypeDeleteLabel') || 'Type DELETE to confirm'}
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            required
            data-testid="delete-account-confirm-text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 uppercase tracking-wider"
          />
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="delete-account-error">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
          <Button type="button" variant="secondary" fullWidth onClick={handleClose} disabled={busy}>
            {t('cancel')}
          </Button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="delete-account-submit"
            className={`w-full sm:flex-1 px-4 py-3 rounded-lg font-medium text-white transition-colors ${
              canSubmit ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'
            }`}
          >
            {busy
              ? (t('deleteAccountProgress') || 'Deleting…')
              : (t('deleteAccountBtn') || 'Delete my account')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DeleteAccountModal;
